import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { ClickToCall } from '@/components/ClickToCall';
import { Mail, Phone as PhoneIcon } from 'lucide-react';
import SuggestedContactsBlock, { ColoredAvatar } from '@/components/SuggestedContactsBlock';

const INDUSTRIES = ['Technology', 'Finance', 'Healthcare', 'Manufacturing', 'Retail', 'Education', 'Real Estate', 'Telecom', 'Media', 'E-commerce', 'Consulting', 'Transportation', 'Hospitality', 'Telecommunications', 'Other'];
const EMPLOYEE_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];

function CompanyDetail() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddStakeholder, setShowAddStakeholder] = useState(false);
  const [showEditCompany, setShowEditCompany] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('stakeholders');
  const [companyContacts, setCompanyContacts] = useState([]);
  const [companyLeads, setCompanyLeads] = useState([]);
  const [companyOpps, setCompanyOpps] = useState([]);

  const [stakeholderForm, setStakeholderForm] = useState({
    name: '',
    designation: '',
    department: '',
    email: '',
    phone: '',
    linkedin_url: '',
    is_decision_maker: false,
    is_primary_contact: false,
    influence_level: 'medium',
    deal_value: '',
    notes: '',
  });

  const [companyForm, setCompanyForm] = useState({});

  const fetchCompany = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/companies/${companyId}`);
      setCompany(response.data);
      setCompanyForm(response.data);
    } catch (error) {
      console.error('Error fetching company:', error);
      setMessage({ type: 'error', text: 'Company not found' });
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchCompany();
    // Fetch leads, opportunities, AND contacts for this company
    // Contacts include both stakeholders added explicitly + lead-POCs that auto-created contacts.
    axios.get(`${API}/companies/${companyId}/leads`).then(r => setCompanyLeads(r.data || [])).catch(() => {});
    axios.get(`${API}/companies/${companyId}/opportunities`).then(r => setCompanyOpps(r.data || [])).catch(() => {});
    axios.get(`${API}/contacts?company_id=${companyId}&limit=50`).then(r => {
      const payload = r.data;
      const list = Array.isArray(payload) ? payload : (payload?.data || []);
      setCompanyContacts(list);
    }).catch(() => {});
  }, [fetchCompany, companyId]);

  // Refresh just contacts + company stakeholders — used after accepting suggestions
  const refreshContacts = useCallback(() => {
    axios.get(`${API}/contacts?company_id=${companyId}&limit=50`).then(r => {
      const payload = r.data;
      const list = Array.isArray(payload) ? payload : (payload?.data || []);
      setCompanyContacts(list);
    }).catch(() => {});
    fetchCompany();
  }, [companyId, fetchCompany]);

  const handleAddStakeholder = async (e) => {
    e.preventDefault();
    if (!stakeholderForm.name) {
      setMessage({ type: 'error', text: 'Stakeholder name is required' });
      return;
    }

    try {
      const submitData = {
        ...stakeholderForm,
        company_id: companyId,
        deal_value: stakeholderForm.deal_value ? parseFloat(stakeholderForm.deal_value) : null,
      };
      await axios.post(`${API}/stakeholders`, submitData);
      setMessage({ type: 'success', text: 'Stakeholder added successfully!' });
      setShowAddStakeholder(false);
      setStakeholderForm({
        name: '', designation: '', department: '', email: '', phone: '',
        linkedin_url: '', is_decision_maker: false, is_primary_contact: false,
        influence_level: 'medium', deal_value: '', notes: '',
      });
      fetchCompany();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to add stakeholder' });
    }
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/companies/${companyId}`, companyForm);
      setMessage({ type: 'success', text: 'Company updated successfully!' });
      setShowEditCompany(false);
      fetchCompany();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update company' });
    }
  };

  const handleDeleteCompany = async () => {
    if (!window.confirm('Are you sure you want to delete this company and all its stakeholders?')) return;
    
    try {
      await axios.delete(`${API}/companies/${companyId}`);
      navigate('/companies');
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete company' });
    }
  };

  const handleLinkToLead = async (stakeholderId) => {
    try {
      const response = await axios.post(`${API}/stakeholders/${stakeholderId}/link-lead`, {});
      setMessage({ type: 'success', text: 'Lead created and linked!' });
      fetchCompany();
      // Refresh contacts so the lead_id badge updates on the stakeholder row
      axios.get(`${API}/contacts?company_id=${companyId}&limit=50`).then(r => {
        const payload = r.data;
        setCompanyContacts(Array.isArray(payload) ? payload : (payload?.data || []));
      }).catch(() => {});
      if (response.data?.lead_id) {
        setTimeout(() => navigate(`/leads/${response.data.lead_id}`), 1500);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      setMessage({
        type: 'error',
        text: typeof detail === 'string' ? detail : 'Failed to create lead',
      });
    }
  };

  const handleDeleteStakeholder = async (stakeholderId, stakeholderName) => {
    if (!window.confirm(`Delete ${stakeholderName || 'this stakeholder'}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/stakeholders/${stakeholderId}`);
      setMessage({ type: 'success', text: 'Stakeholder deleted' });
      fetchCompany();
      axios.get(`${API}/contacts?company_id=${companyId}&limit=50`).then(r => {
        const payload = r.data;
        setCompanyContacts(Array.isArray(payload) ? payload : (payload?.data || []));
      }).catch(() => {});
    } catch (error) {
      const detail = error.response?.data?.detail;
      setMessage({
        type: 'error',
        text: typeof detail === 'string' ? detail : 'Failed to delete stakeholder',
      });
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getInfluenceColor = (level) => {
    const colors = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800',
    };
    return colors[level] || 'bg-slate-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="company-detail-loading">
        <div className="animate-spin w-8 h-8 border-2 border-slate-200 dark:border-slate-700 border-t-zinc-900 rounded-full"></div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Company not found</h2>
        <Link to="/companies" className="text-blue-600 hover:text-slate-800 dark:hover:text-slate-200 dark:text-slate-100 mt-4 inline-block">
          Back to Companies
        </Link>
      </div>
    );
  }

  // Merge embedded stakeholders[] (added via "Add Stakeholder" button)
  // with contacts that have company_id == this company (auto-created from Leads etc.)
  // Dedupe by email (case-insensitive).
  const embeddedStakeholders = company.stakeholders || [];
  const contactStakeholders = (companyContacts || []).map(ct => ({
    id: ct.id,
    contact_id: ct.id,
    name: ct.name,
    email: ct.email,
    phone: ct.phone,
    designation: ct.designation || ct.role || '',
    is_primary: ct.is_primary || ct.is_primary_contact || false,
    is_primary_contact: ct.is_primary || ct.is_primary_contact || false,
    is_decision_maker: ct.is_decision_maker || false,
    influence_level: ct.influence_level,
    lead_id: ct.lead_id,
    source: ct.auto_created_from_lead ? 'lead' : 'direct',
    added_at: ct.created_at,
  }));
  const stakeholderMap = new Map();
  [...embeddedStakeholders, ...contactStakeholders].forEach(s => {
    const key = (s.email || s.contact_id || s.name || '').toLowerCase().trim();
    if (!key) return;
    const existing = stakeholderMap.get(key);
    if (existing) {
      // Prefer the record with more info — merge flags
      stakeholderMap.set(key, {
        ...existing,
        ...s,
        is_primary_contact: existing.is_primary_contact || s.is_primary_contact,
        is_decision_maker: existing.is_decision_maker || s.is_decision_maker,
        contact_id: existing.contact_id || s.contact_id,
      });
    } else {
      stakeholderMap.set(key, s);
    }
  });
  const stakeholders = Array.from(stakeholderMap.values());
  const decisionMakers = stakeholders.filter(s => s.is_decision_maker);
  const primaryContact = stakeholders.find(s => s.is_primary_contact);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 -m-4 lg:-m-6 p-4 lg:p-6 space-y-5" data-testid="company-detail-page">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between">
          <div className="flex items-start space-x-4">
            <div className="h-14 w-14 rounded-xl bg-slate-900 flex items-center justify-center text-white font-bold text-xl">
              {company.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">{company.name}</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">{company.industry || 'No industry specified'}</p>
              {company.city && (
                <p className="text-sm text-slate-400 mt-1">
                  {company.city}, {company.state || company.country}
                </p>
              )}
            </div>
          </div>

          <div className="flex space-x-2 mt-4 md:mt-0">
            <button
              onClick={() => setShowEditCompany(true)}
              className="px-3 py-2 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900 transition-all text-sm flex items-center"
              data-testid="edit-company-btn"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>
            <button
              onClick={handleDeleteCompany}
              className="px-3 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-all text-sm flex items-center"
              data-testid="delete-company-btn"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          </div>
        </div>

        {/* Company Stats - Clean KPI Style with BLACK values */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3 text-center" data-testid="stakeholders-stat">
            <p className="text-xl font-bold text-slate-900 dark:text-white">{stakeholders.length}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1">Stakeholders</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3 text-center" data-testid="total-pipeline-stat">
            <p className="text-xl font-bold text-slate-900 dark:text-white">{formatCurrency(company.total_deal_value)}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1">Total Pipeline</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3 text-center" data-testid="decision-makers-stat">
            <p className="text-xl font-bold text-slate-900 dark:text-white">{decisionMakers.length}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1">Decision Makers</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3 text-center" data-testid="employees-stat">
            <p className="text-xl font-bold text-slate-900 dark:text-white">{company.employee_size || '-'}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1">Employees</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3 text-center" data-testid="annual-revenue-stat">
            <p className="text-xl font-bold text-slate-900 dark:text-white">{company.annual_revenue ? formatCurrency(company.annual_revenue) : '-'}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1">Annual Revenue</p>
          </div>
        </div>
      </div>

      {message.text && (
        <div className={`px-4 py-3 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
        <div className="border-b border-slate-100 dark:border-slate-700">
          <nav className="flex -mb-px overflow-x-auto px-4" data-testid="tabs-navigation">
            {['stakeholders', 'leads', 'opportunities', 'details', 'activities'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-all whitespace-nowrap capitalize ${
                  activeTab === tab
                    ? 'border-slate-900 text-slate-900 dark:text-white'
                    : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 dark:text-slate-300 hover:border-slate-300 dark:border-slate-600'
                }`}
                data-testid={`tab-${tab}`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Stakeholders Tab */}
          {activeTab === 'stakeholders' && (
            <div>
              {/* ── Suggested contacts (Salesflare-style auto-discovery) ── */}
              <SuggestedContactsBlock companyId={companyId} onContactsChanged={refreshContacts} />

              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                  Stakeholders ({stakeholders.length})
                </h3>
                <button
                  onClick={() => setShowAddStakeholder(true)}
                  className="px-3 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-all flex items-center text-sm font-medium"
                  data-testid="add-stakeholder-btn"
                >
                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Stakeholder
                </button>
              </div>

              {primaryContact && (
                <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg">
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100 mb-2">Primary Contact</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <ColoredAvatar name={primaryContact.name} email={primaryContact.email} size={40} />
                      <div className="ml-3">
                        <p className="font-medium text-slate-900 dark:text-white">{primaryContact.name}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{primaryContact.designation || 'No designation'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {primaryContact.email && <p className="text-sm text-slate-600 dark:text-slate-400">{primaryContact.email}</p>}
                      {primaryContact.phone && (
                        <div className="flex items-center justify-end gap-2">
                          <span className="text-sm text-slate-600 dark:text-slate-400">{primaryContact.phone}</span>
                          <ClickToCall 
                            phone={primaryContact.phone} 
                            leadName={primaryContact.name}
                            variant="icon"
                            size="sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {stakeholders.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No Stakeholders</h3>
                  <p className="text-slate-500 dark:text-slate-400 mb-4">Add contacts from this company to track relationships.</p>
                  <button
                    onClick={() => setShowAddStakeholder(true)}
                    className="btn-primary transition"
                  >
                    Add First Stakeholder
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {stakeholders.map((stakeholder) => (
                    <div
                      key={stakeholder.id}
                      className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition-all"
                      data-testid={`stakeholder-card-${stakeholder.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                          <ColoredAvatar name={stakeholder.name} email={stakeholder.email} size={40} />
                          <div>
                            <div className="flex items-center space-x-2">
                              <h4 className="font-semibold text-slate-900 dark:text-white">{stakeholder.name}</h4>
                              {stakeholder.is_decision_maker && (
                                <span className="px-2 py-0.5 text-xs bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300 rounded-full">Decision Maker</span>
                              )}
                              {stakeholder.is_primary_contact && (
                                <span className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 rounded-full">Primary</span>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              {stakeholder.designation || 'No designation'}
                              {stakeholder.department && ` · ${stakeholder.department}`}
                            </p>
                            <div className="flex items-center space-x-4 mt-2 text-sm text-slate-600 dark:text-slate-400 flex-wrap">
                              {stakeholder.email && (
                                <span className="inline-flex items-center gap-1.5">
                                  <Mail className="w-3.5 h-3.5 text-slate-400" />
                                  <span className="truncate max-w-[280px]">{stakeholder.email}</span>
                                  <a
                                    href={`/email?compose=1&to=${encodeURIComponent(stakeholder.email)}`}
                                    className="text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded p-0.5 transition-colors"
                                    title={`Email ${stakeholder.name}`}
                                    data-testid={`email-stakeholder-${stakeholder.id}`}
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                    </svg>
                                  </a>
                                </span>
                              )}
                              {stakeholder.phone && (
                                <span className="inline-flex items-center gap-1.5">
                                  <PhoneIcon className="w-3.5 h-3.5 text-slate-400" />
                                  {stakeholder.phone}
                                  <ClickToCall
                                    phone={stakeholder.phone}
                                    leadName={stakeholder.name}
                                    variant="icon"
                                    size="sm"
                                  />
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col items-end space-y-2">
                          <div className="flex items-center space-x-2">
                            {stakeholder.influence_level && (
                              <span className={`px-2 py-1 text-xs rounded-full ${getInfluenceColor(stakeholder.influence_level)}`}>
                                {stakeholder.influence_level} influence
                              </span>
                            )}
                            {stakeholder.deal_value && (
                              <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full font-semibold">
                                {formatCurrency(stakeholder.deal_value)}
                              </span>
                            )}
                          </div>
                          <div className="flex space-x-2">
                            {!stakeholder.lead_id ? (
                              <button
                                onClick={() => handleLinkToLead(stakeholder.id)}
                                className="text-xs text-blue-600 hover:text-slate-800 dark:hover:text-slate-200 dark:text-slate-100"
                                data-testid={`link-lead-${stakeholder.id}`}
                              >
                                Create Lead
                              </button>
                            ) : (
                              <Link
                                to={`/leads/${stakeholder.lead_id}`}
                                className="text-xs text-blue-600 hover:text-slate-800 dark:hover:text-slate-200 dark:text-slate-100"
                              >
                                View Lead
                              </Link>
                            )}
                            <button
                              onClick={() => handleDeleteStakeholder(stakeholder.id, stakeholder.name)}
                              className="text-xs text-red-600 hover:text-red-800"
                              data-testid={`delete-stakeholder-${stakeholder.id}`}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>

                      {stakeholder.notes && (
                        <p className="mt-3 text-sm text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 p-2 rounded">{stakeholder.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Leads Tab */}
          {activeTab === 'leads' && (
            <div data-testid="company-leads-tab">
              {companyLeads.length === 0 ? (
                <div className="text-center py-12 text-slate-400 dark:text-slate-500">
                  <p className="text-sm">No leads linked to this company yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {companyLeads.map(lead => {
                    const nad = lead.next_action_date;
                    let nadColor = '';
                    if (nad) {
                      const diff = Math.floor((new Date(nad) - new Date(new Date().toDateString())) / 86400000);
                      nadColor = diff < 0 ? 'text-red-600 dark:text-red-400' : diff === 0 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400';
                    }
                    return (
                      <Link key={lead.id} to={`/leads/${lead.id}`} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid={`company-lead-${lead.id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs font-bold">
                            {lead.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-800 dark:text-white">{lead.name}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">{lead.email || lead.phone || 'No contact'} · {lead.status}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {lead.deal_value > 0 && <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">₹{lead.deal_value?.toLocaleString('en-IN')}</p>}
                          {nad && <p className={`text-[10px] font-medium ${nadColor}`}>NAD: {new Date(nad).toLocaleDateString('en-IN', {day:'numeric',month:'short'})}</p>}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Opportunities Tab */}
          {activeTab === 'opportunities' && (
            <div data-testid="company-opps-tab">
              {companyOpps.length === 0 ? (
                <div className="text-center py-12 text-slate-400 dark:text-slate-500">
                  <p className="text-sm">No opportunities linked to this company yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {companyOpps.map(opp => {
                    const stageColors = { qualification: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', discovery: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300', meeting: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300', proposal: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', negotiation: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300', closed_won: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300', closed_lost: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400' };
                    return (
                      <Link key={opp.id} to={`/opportunities/${opp.id}`} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid={`company-opp-${opp.id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/40 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-800 dark:text-white">{opp.name}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${stageColors[opp.stage] || stageColors.qualification}`}>{opp.stage?.replace('_',' ')}</span>
                              {opp.probability && <span className="text-[10px] text-slate-400">{opp.probability}%</span>}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">₹{(opp.deal_value || 0).toLocaleString('en-IN')}</p>
                          {opp.expected_close_date && <p className="text-[10px] text-slate-400">Close: {new Date(opp.expected_close_date).toLocaleDateString('en-IN', {day:'numeric',month:'short'})}</p>}
                        </div>
                      </Link>
                    );
                  })}
                  <div className="pt-2 text-right">
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                      Total Pipeline: <span className="text-emerald-600 dark:text-emerald-400 font-bold">₹{companyOpps.reduce((s, o) => s + (o.deal_value || 0), 0).toLocaleString('en-IN')}</span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4">
                <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">Contact Information</h4>
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className="text-xs text-slate-400">Website</dt>
                    <dd className="text-sm text-slate-900 dark:text-white">
                      {company.website ? (
                        <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          {company.website}
                        </a>
                      ) : '-'}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-xs text-slate-400">LinkedIn</dt>
                    <dd className="text-sm text-slate-900 dark:text-white">
                      {company.linkedin_url ? (
                        <a href={company.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          View Profile
                        </a>
                      ) : '-'}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-xs text-slate-400">Phone</dt>
                    <dd className="text-sm text-slate-900 dark:text-white">{company.phone || '-'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-xs text-slate-400">Email</dt>
                    <dd className="text-sm text-slate-900 dark:text-white">{company.email || '-'}</dd>
                  </div>
                </dl>
              </div>

              <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4">
                <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">Address</h4>
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className="text-xs text-slate-400">Address</dt>
                    <dd className="text-sm text-slate-900 dark:text-white">{company.address || '-'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-xs text-slate-400">City</dt>
                    <dd className="text-sm text-slate-900 dark:text-white">{company.city || '-'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-xs text-slate-400">State</dt>
                    <dd className="text-sm text-slate-900 dark:text-white">{company.state || '-'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-xs text-slate-400">Country</dt>
                    <dd className="text-sm text-slate-900 dark:text-white">{company.country || 'India'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-xs text-slate-400">Pincode</dt>
                    <dd className="text-sm text-slate-900 dark:text-white">{company.pincode || '-'}</dd>
                  </div>
                </dl>
              </div>

              {company.description && (
                <div className="md:col-span-2">
                  <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Description</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">{company.description}</p>
                </div>
              )}
            </div>
          )}

          {/* Activities Tab */}
          {activeTab === 'activities' && (
            <div>
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-6">Recent Activities</h3>
              {company.recent_activities && company.recent_activities.length > 0 ? (
                <div className="space-y-3">
                  {company.recent_activities.map((activity) => (
                    <div key={activity.id} className="flex items-start space-x-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                      <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center">
                        <svg className="w-4 h-4 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900 dark:text-white capitalize">{activity.activity_type}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {activity.created_at ? format(new Date(activity.created_at), 'MMM dd, yyyy HH:mm') : '-'}
                        </p>
                        {activity.notes && <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{activity.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 dark:text-slate-400 text-center py-8">No recent activities</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Stakeholder Modal */}
      {showAddStakeholder && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-lg w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Add Stakeholder</h3>
            <form onSubmit={handleAddStakeholder} className="space-y-4">
              <div>
                <label className="block form-label">Name *</label>
                <input
                  type="text"
                  value={stakeholderForm.name}
                  onChange={(e) => setStakeholderForm({ ...stakeholderForm, name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                  placeholder="John Doe"
                  data-testid="stakeholder-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block form-label">Designation</label>
                  <input
                    type="text"
                    value={stakeholderForm.designation}
                    onChange={(e) => setStakeholderForm({ ...stakeholderForm, designation: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                    placeholder="CTO"
                  />
                </div>
                <div>
                  <label className="block form-label">Department</label>
                  <input
                    type="text"
                    value={stakeholderForm.department}
                    onChange={(e) => setStakeholderForm({ ...stakeholderForm, department: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                    placeholder="Technology"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block form-label">Email</label>
                  <input
                    type="email"
                    value={stakeholderForm.email}
                    onChange={(e) => setStakeholderForm({ ...stakeholderForm, email: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                    placeholder="john@company.com"
                  />
                </div>
                <div>
                  <label className="block form-label">Phone</label>
                  <input
                    type="tel"
                    value={stakeholderForm.phone}
                    onChange={(e) => setStakeholderForm({ ...stakeholderForm, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                    placeholder="+91-9876543210"
                  />
                </div>
              </div>

              <div>
                <label className="block form-label">Deal Value (INR)</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-slate-500 dark:text-slate-400">₹</span>
                  <input
                    type="number"
                    value={stakeholderForm.deal_value}
                    onChange={(e) => setStakeholderForm({ ...stakeholderForm, deal_value: e.target.value })}
                    className="w-full pl-8 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                    placeholder="500000"
                  />
                </div>
              </div>

              <div>
                <label className="block form-label">Influence Level</label>
                <select
                  value={stakeholderForm.influence_level}
                  onChange={(e) => setStakeholderForm({ ...stakeholderForm, influence_level: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              <div className="flex space-x-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={stakeholderForm.is_decision_maker}
                    onChange={(e) => setStakeholderForm({ ...stakeholderForm, is_decision_maker: e.target.checked })}
                    className="h-4 w-4 text-blue-600 border-slate-300 dark:border-slate-600 rounded"
                  />
                  <span className="ml-2 text-sm text-slate-700 dark:text-slate-300">Decision Maker</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={stakeholderForm.is_primary_contact}
                    onChange={(e) => setStakeholderForm({ ...stakeholderForm, is_primary_contact: e.target.checked })}
                    className="h-4 w-4 text-blue-600 border-slate-300 dark:border-slate-600 rounded"
                  />
                  <span className="ml-2 text-sm text-slate-700 dark:text-slate-300">Primary Contact</span>
                </label>
              </div>

              <div>
                <label className="block form-label">Notes</label>
                <textarea
                  value={stakeholderForm.notes}
                  onChange={(e) => setStakeholderForm({ ...stakeholderForm, notes: e.target.value })}
                  rows="2"
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                  placeholder="Additional notes about this contact..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddStakeholder(false)}
                  className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-slate-900 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700"
                  data-testid="save-stakeholder-btn"
                >
                  Add Stakeholder
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Company Modal */}
      {showEditCompany && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-2xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Edit Company</h3>
            <form onSubmit={handleUpdateCompany} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block form-label">Company Name</label>
                  <input
                    type="text"
                    value={companyForm.name || ''}
                    onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block form-label">Industry</label>
                  <select
                    value={companyForm.industry || ''}
                    onChange={(e) => setCompanyForm({ ...companyForm, industry: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    data-testid="edit-company-industry"
                  >
                    <option value="">Select Industry</option>
                    {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block form-label">Employee Size</label>
                  <select
                    value={companyForm.employee_size || ''}
                    onChange={(e) => setCompanyForm({ ...companyForm, employee_size: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    data-testid="edit-company-employee-size"
                  >
                    <option value="">Select Size</option>
                    {EMPLOYEE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block form-label">Website</label>
                  <input
                    type="url"
                    value={companyForm.website || ''}
                    onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block form-label">Phone</label>
                  <input
                    type="tel"
                    value={companyForm.phone || ''}
                    onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block form-label">City</label>
                  <input
                    type="text"
                    value={companyForm.city || ''}
                    onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block form-label">State</label>
                  <input
                    type="text"
                    value={companyForm.state || ''}
                    onChange={(e) => setCompanyForm({ ...companyForm, state: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditCompany(false)}
                  className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-slate-900 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyDetail;
